import { useQuery, useSuspenseQuery } from '@tanstack/react-query'

import { favoriteReportsService, reportsService } from '../../api/services/reports.service'
import { UnauthorizedError } from '../../auth/UnauthorizedError'
import { clearAuth } from '../../store/authSlice'
import { useAppDispatch, useCurrentSite } from '../../store/store.hooks'
import { Report } from '../../types'
import ReportsQueries from './ReportsQueries'

const EMPTY_FAVORITE_IDS: string[] = []

function compareReports(a: Report, b: Report) {
  if (a.reportType > b.reportType) {
    return -1
  }
  if (a.reportType < b.reportType) {
    return 1
  }
  return a.name.localeCompare(b.name)
}

export const useReportList = () => {
  const currentSite = useCurrentSite()

  const { data } = useSuspenseQuery({
    queryKey: ['reports', 'list', currentSite?.id],
    queryFn: () => reportsService.list(currentSite?.id ?? ''),
    select: (reports) => [...reports].sort(compareReports),
  })

  return { reports: data ?? [] }
}

export const useFavoriteReportIds = () => {
  const currentSite = useCurrentSite()

  const { data } = useSuspenseQuery({
    queryKey: ['reports', 'favorites', currentSite?.id],
    queryFn: () => favoriteReportsService.list(currentSite?.id ?? ''),
    staleTime: 0,
    refetchOnMount: 'always',
    select: (reports) => reports.map((report) => report.id),
  })

  return data ?? EMPTY_FAVORITE_IDS
}

export const useAdminReportList = () => {
  const { name, request } = ReportsQueries.listAdminReports

  const { data } = useSuspenseQuery({
    queryKey: name,
    queryFn: () => request(),
    select: (reports) => reports.sort(compareReports),
  })

  return { reports: data }
}

export const useReportParams = (workspaceId: string, reportId: string) => {
  const dispatch = useAppDispatch()
  const { name, request } = ReportsQueries.getReportParams
  const { data, error } = useQuery({
    queryKey: [...name, workspaceId, reportId],
    queryFn: () => request(workspaceId, reportId),
    gcTime: 0,
  })

  if (error instanceof UnauthorizedError) {
    dispatch(clearAuth())
  }

  return { reportParams: data, error }
}
export const useAdminReportParams = (workspaceId: string, reportId: string) => {
  const dispatch = useAppDispatch()
  const { name, request } = ReportsQueries.getAdminReportParams
  const { data, error } = useQuery({
    queryKey: [...name, workspaceId, reportId],
    queryFn: () => request(workspaceId, reportId),
    gcTime: 0,
  })

  if (error instanceof UnauthorizedError) {
    dispatch(clearAuth())
  }

  return { reportParams: data, error }
}
